import { useEffect, useState } from "react";
import { Box, Fade, Paper, ToggleButton, ToggleButtonGroup, } from "@mui/material";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import GradingIcon from '@mui/icons-material/Grading';
import { MapkitProvider } from "react-mapkit";
import { AppColor, ModerationViewMode } from "../../types/Constants";
import { Beacon } from "../../types/models/Beacon";
import { BeaconColumnInfo, BeaconModerationColumnKeys, } from "../beacons/pages/list/BeaconListTypes";
import BeaconList from "../beacons/pages/list/BeaconList";
import BeaconDialog from "../beacons/components/creatingBeacon/BeaconDialog";
import MapView from "../beacons/pages/map/MapView";
import QueuedReportList from "./queuedReportList/QueuedReportList";
import QueuedReportListAction from "./queuedReportList/QueuedReportListAction";
import { useAppDispatch } from "../../utils/hooks";
import { getMapkitToken } from "../../redux/actions";

function ModerationIndex() {
  // Util
  const dispatch = useAppDispatch();

  // State
  const [beaconListInfo, setBeaconListInfo] = useState<BeaconColumnInfo[]>([]);
  const [refreshDataFlag, setRefreshDataFlag] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<string>(ModerationViewMode.List);
  const [selectedBeacon, setSelectedBeacon] = useState<Beacon>();
  const [openBeaconDialog, setOpenBeaconDialog] = useState<boolean>(false);
  const [openDeleteBeacon, setOpenDeleteBeacon] = useState<boolean>(false);
  const [beaconMapProps, setBeaconMapProps] = useState<{ tokenOrCallback: string }>();

  // Effects
  useEffect(() => {
    /*
      Generate Column data for the table. Mod view has fixed columns, 
      but pattern kept similar for consistency.
    */
    let columns: BeaconColumnInfo[] = [];
    BeaconModerationColumnKeys.map((value: string) =>
      columns.push({ columnType: value, visibility: true })
    );

    setBeaconListInfo(columns);
  }, []);

  useEffect(() => {
    if (viewMode === ModerationViewMode.Map) {
      dispatch(
        getMapkitToken((success, token) => {
          if (success && token) {
            setBeaconMapProps({ tokenOrCallback: token });
          }
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Functions
  const handleOpenBeaconDialog = (beacon?: Beacon) => {
    setSelectedBeacon(beacon);
    setOpenBeaconDialog(true);
  };

  const handleOpenBeaconDeleteDialog = (beacon?: Beacon) => {
    setOpenDeleteBeacon(true);
    setSelectedBeacon(beacon);
  };

  const handleOpenBeaconLink = (beacon?: Beacon) => {
    if(beacon?.link){
      const url = beacon?.link;
      window.open(url, "_blank", "noreferrer");
    }
  };

  // Render content depending on the viewmode.
  const renderTabContent = () => {
    switch (viewMode) {
      case ModerationViewMode.List:
        return (
          <Paper
            sx={{
              m: 1,
              backgroundColor: "white",
              overflowY: "scroll",
              height: "calc(100vh - 130px)"
            }}
            elevation={1}
          >
            {/* <BeaconList
              context="MODERATION"
              beaconColumnInfo={beaconListInfo}
              refreshFlag={refreshDataFlag}
              setRefreshFlag={setRefreshDataFlag}
              onOpenBeaconDialog={handleOpenBeaconDialog}
              onOpenBeaconDeleteDialog={handleOpenBeaconDeleteDialog}
              onOpenBeaconLink={handleOpenBeaconLink}
              isArchivedView={false}
            /> */}
          </Paper>
        );

      case ModerationViewMode.Map:
        return (
          <Fade in={true}>
            <Paper sx={{ m: 0, backgroundColor: "white" }} elevation={1}>
              {beaconMapProps && (
                <MapkitProvider {...beaconMapProps}>
                  {/* <MapView
                    onOpenBeaconDialog={handleOpenBeaconDialog}
                    refreshFlag={refreshDataFlag}
                    setRefreshFlag={setRefreshDataFlag}
                    context="MODERATION"
                  /> */}
                </MapkitProvider>
              )}
            </Paper>
          </Fade>
        );
      case ModerationViewMode.Report:
        return (
          <Fade in={true}>
            <Paper sx={{
              mx: 1,
              backgroundColor: "white",
              overflowY: "scroll",
              height: "calc(100vh - 124px)"
            }}
              elevation={1}>
              <QueuedReportList />
            </Paper>
          </Fade>
        );
      default:
    }
  };

  // Final Render
  return (
    <Box
      sx={{ maxHeight: "100vh", padding: 1, backgroundColor: AppColor.componentBackground }}
      display="flex"
      flexDirection="column"
      alignContent="stretch"
    >
      {/* Edit Beacon Dialog */}
      <BeaconDialog
        onSubmitted={() => { }}
        mode={"MOD_ONLY"} /* Always MOD_ONLY for moderators. */
        isOpen={openBeaconDialog}
        beacon={selectedBeacon}
        onClose={() => setOpenBeaconDialog(false)}
      />

      {/* Data View Controls */}
      <Box sx={{ margin: 2, display: "flex", justifyContent: "space-between" }}>
        <ToggleButtonGroup
          exclusive
          value={viewMode}
          onChange={(event, newValue) => {
            if (newValue) {
              setViewMode(newValue);
            }
          }}
          aria-label="Platform"
          sx={{
            flexWrap: "nowrap",
            "& .MuiToggleButton-root": {
              border: "none",
              backgroundColor: AppColor.componentBackground,
              textTransform: "none",
              padding: "6px",
            },
            "& .Mui-selected": {
              color: AppColor.baseOrange + " !important",
              backgroundColor: AppColor.orangeBackground + " !important"
            }
          }}
        >
          <ToggleButton tabIndex={0} value={ModerationViewMode.List}>
            <FormatListBulletedOutlinedIcon sx={{ mr: 1 }} />
            List
          </ToggleButton>
          <ToggleButton tabIndex={1} value={ModerationViewMode.Map}>
            <MapOutlinedIcon sx={{ mr: 1 }} />
            Map
          </ToggleButton>
          <ToggleButton tabIndex={2} value={ModerationViewMode.Report}>
            <GradingIcon sx={{ mr: 1 }} />
            Report
          </ToggleButton>
        </ToggleButtonGroup>
        <Box>
          {viewMode === ModerationViewMode.Report &&
            <QueuedReportListAction />
          }
        </Box>
      </Box>

      {/* Render content depending on selected view mode. */}
      {renderTabContent()}
    </Box>
  );
}

export default ModerationIndex;